'use client'

import { useState, useEffect } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { getReadableImageUrl } from '@/lib/directus'
import { sanitizeHTML } from '@/lib/utils'

interface HomeProcProps {
	background?: {
		alt: string
		image_file: {
			image: {
				id: string
				filename_download: string
			}
		}
	}
	content_collection?: {
		id: string
		site_content_collections_id: {
			collection: {
				item: {
					title?: string
					text?: string
					url?: string
				}
			}[]
		}
	}[]
}

export const fields = [
	'background.alt',
	'background.image_file.image.id', 
	'background.image_file.image.filename_download',

	'content_collection.id',
	'content_collection.site_content_collections_id.collection.item:site_content.title',
	'content_collection.site_content_collections_id.collection.item:site_content.text',
	'content_collection.site_content_collections_id.collection.item:external_links.url',
]

const ArrowIcon = ({ isActive }: { isActive: boolean }) => (
  <svg 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    className={`transition-transform duration-300 ${isActive ? 'rotate-90' : ''}`}
  >
    <path 
      d="M9 18L15 12L9 6" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
)

export default function HomeProc({ data }: { data: HomeProcProps }) {
  const [activeId, setActiveId] = useState<number | null>(null)
  const [isMobileExpanded, setIsMobileExpanded] = useState(false)
  const [windowHeight, setWindowHeight] = useState(0)

  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight)
    }

    // Set initial height
    handleResize()

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleClick = (id: number) => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      setActiveId(id)
      setIsMobileExpanded(true)
    } else {
      setActiveId(activeId === id ? null : id)
    }
  }

  const handleBack = () => {
    setIsMobileExpanded(false)
  }

  const renderProceduresList = (isMobile: boolean) => (
		<div className={isMobile ? 'space-y-4' : 'space-y-8'}>
			{data.content_collection?.map((item, index) => (
				<div
					key={item.id || index}
					className={
						isMobile ? 'border-b border-white/20 last:border-b-0' : 'group'
					}
				>
					<button
						onClick={() => handleClick(index + 1)}
						className={`cursorrules flex items-center justify-between w-full ${
							isMobile
								? 'py-4'
								: 'text-left group-hover:opacity-80 transition-opacity'
						}`}
					>
						<h2
							className={`font-agopus text-white tracking-wide ${
								isMobile ? 'text-3xl' : 'text-5xl'
							}`}
						>
							{item.site_content_collections_id?.collection[0]?.item?.title ||
								''}
						</h2>
						<div className='text-white'>
							<ArrowIcon isActive={!isMobile && activeId === index + 1} />
						</div>
					</button>

					{!isMobile && (
						<div
							className={`grid transition-all duration-500 ease-in-out ${
								activeId === index + 1
									? 'grid-rows-[1fr] opacity-100'
									: 'grid-rows-[0fr] opacity-0'
							}`}
						>
							<div className='overflow-hidden'>
								<div className='pt-6 space-y-6'>
									<div
										className={`text_box_desk text-white max-w-[30rem] leading-relaxed prose prose-white ${
											windowHeight < 850
												? '[&>p]:text-lg [&>p]:leading-[1.4rem]'
												: ''
										}`}
										dangerouslySetInnerHTML={sanitizeHTML(
											item.site_content_collections_id?.collection[1]?.item
												?.text || ''
										)}
									/>
									<Link
										href={
											item.site_content_collections_id?.collection[2]?.item
												?.url || ''
										}
										className='cursorrules inline-block bg-brown hover:bg-brown-light px-8 py-4 rounded-lg text-white font-brandon text-xl transition-colors'
									>
										Read more about{' '}
										{item.site_content_collections_id?.collection[0]?.item
											?.title || ''}
									</Link>
								</div>
							</div>
						</div>
					)}
				</div>
			))}
		</div>
	)

  return (
		<section className='HomeProc relative h-screen w-full overflow-hidden max-md:rounded-tl-[50px]'>
			{/* Background Image with Overlay */}
			<div className='box_img_bg absolute inset-0'>
				<Image
					src={getReadableImageUrl(
						data.background?.image_file?.image?.id || '',
						data.background?.image_file?.image?.filename_download || ''
					)}
					alt={data.background?.alt || ''}
					fill
					className='object-cover md:object-center object-[46%_0]'
					priority
					quality={96}
				/>
				<div className='absolute inset-0 bg-black/40' />
			</div>

			{/* Desktop Content */}
			<div className='hidden md:flex relative h-full items-center container'>
				<div className='max-w-[48rem] space-y-29'>
					<div className='relative w-20 h-20'>
						<Image
							src='/images/logo_h.svg'
							alt='Harmon Logo'
							fill
							sizes='5rem'
							className='object-contain'
							priority
						/>
					</div>
					{renderProceduresList(false)}
				</div>
			</div>

			{/* Mobile Content */}
			<div className='md:hidden relative h-full flex flex-col'>
				<div className='relative z-10 p-7'>
					<div className='relative w-12 h-12'>
						<Image
							src='/images/logo_h.svg'
							alt='Harmon Logo'
							fill
							sizes='3rem'
							className='object-contain'
							priority
							quality={90}
						/>
					</div>
				</div>

				<div className='mt-auto relative z-10 p-6'>
					{renderProceduresList(true)}
				</div>

				{/* Mobile Expanded View */}
				<div
					className={`fixed inset-0 top-[4.5rem] bg-[#657073]/75 z-20 transition-transform duration-500 ${
						isMobileExpanded ? 'translate-y-0' : 'translate-y-full'
					}`}
				>
					<div className='h-full flex flex-col p-4'>
						<button
							onClick={handleBack}
							className='cursorrules flex items-center text-white mb-8'
						>
							<div className='flex items-center justify-center w-10 h-10 rounded-full border border-white'>
								<svg
									width='40'
									height='40'
									viewBox='0 0 40 40'
									fill='none'
									xmlns='http://www.w3.org/2000/svg'
								>
									<rect
										x='-1'
										y='1'
										width='38'
										height='38'
										rx='19'
										transform='matrix(-1 0 0 1 38 0)'
										stroke='white'
										strokeWidth='2'
									/>
									<path
										d='M7.29289 20.7071C6.90237 20.3166 6.90237 19.6834 7.29289 19.2929L13.6569 12.9289C14.0474 12.5384 14.6805 12.5384 15.0711 12.9289C15.4616 13.3195 15.4616 13.9526 15.0711 14.3431L9.41421 20L15.0711 25.6569C15.4616 26.0474 15.4616 26.6805 15.0711 27.0711C14.6805 27.4616 14.0474 27.4616 13.6569 27.0711L7.29289 20.7071ZM32 21L8 21L8 19L32 19L32 21Z'
										fill='white'
									/>
								</svg>
							</div>
						</button>

						{/* All items in DOM for SEO/indexing; visibility controlled by CSS */}
						{data.content_collection?.map((item, index) => {
							const isActive = activeId === index + 1
							const title =
								item.site_content_collections_id?.collection[0]?.item?.title || ''
							const text =
								item.site_content_collections_id?.collection[1]?.item?.text || ''
							const url =
								item.site_content_collections_id?.collection[2]?.item?.url || ''
							return (
								<div
									key={item.id || index}
									aria-hidden={!isActive}
									className={`flex-1 flex flex-col min-h-0 ${
										isActive ? '' : 'max-h-0 opacity-0 overflow-hidden pointer-events-none'
									}`}
								>
									<p className='text-white mb-6 text-4xl font-agopus'>
										{title}
									</p>
									<div
										className='text-white mb-8 flex-1 prose prose-white [&>p]:leading-[1.4rem]'
										dangerouslySetInnerHTML={sanitizeHTML(text)}
									/>
									<div className='space-y-4'>
										<Link
											href={url}
											className='cursorrules block text-center border border-white px-8 py-4 rounded-lg text-white font-brandon text-base hover:bg-white/10 transition-colors'
										>
											Learn more about {title}
										</Link>
										<Link
											href='/gallery'
											className='cursorrules block text-center border border-white px-8 py-4 rounded-lg text-white font-brandon text-base hover:bg-white/10 transition-colors'
										>
											View Gallery
										</Link>
									</div>
								</div>
							)
						})}
					</div>
				</div>
			</div>
		</section>
	)
}